import Highcharts from 'highcharts'
import MenuButton from './menu'
import HoverArea from './area'
import Tooltip, { CustomPlotlineTooltip } from './tooltip'
import Timestamp from './timestamp'
import { MarkersData } from '../../api'
import { Line } from '../positioners'
import Clusters from '../clusters'

export interface ExtendedPlotLineOptions {
    id: string
    value: number
    data: MarkersData
    onShowDetails: (data: MarkersData, position: Record<string, unknown>) => void
    onChangePosition: (id: string) => void
    isCommited?: boolean
}

export interface ExtendedPlotline extends Highcharts.PlotLineOrBand {
    options: ExtendedPlotLineOptions
    render: () => void
    setFocus: (val: boolean) => void
    focus: boolean
    svgElem: { pathArray: [string, number, number][]; visibility: string; element: SVGElement } & Highcharts.SVGRenderer
    linePath: Line
    menuButton: CustomPlotlineElement
    hoverArea: CustomPlotlineElement
    tooltip: CustomPlotlineTooltip
    timestamp: CustomPlotlineElement
    axis: any
}

export interface CustomPlotlineElement extends Highcharts.SVGElement {
    update: (plotline: ExtendedPlotline, hide: boolean) => Record<string, unknown> | null
    destroy: () => void
}

export default function setUp(plotline: ExtendedPlotline): void {
    const [, ...start] = plotline.svgElem?.pathArray?.[0] || ['', -5, -5]
    const [, ...end] = plotline.svgElem?.pathArray?.[1] || ['', -5, -5]

    plotline.focus = true
    plotline.linePath = { start: { x: start[0], y: start[1] }, end: { x: end[0], y: end[1] } }
    plotline.menuButton = MenuButton(plotline)
    plotline.hoverArea = HoverArea(plotline)

    // Trigger tooltip first render
    plotline.tooltip = Tooltip(plotline) as CustomPlotlineTooltip
    plotline.tooltip.update(plotline, false)

    plotline.timestamp = Timestamp(plotline)

    plotline.render = function (...args) {
        const result: ExtendedPlotline = Object.getPrototypeOf(this).render.call(this, ...args)

        const [, ...start] = result.svgElem?.pathArray?.[0] || ['', -5, -5]
        const [, ...end] = result.svgElem?.pathArray?.[1] || ['', -5, -5]

        // consider destroying when that happens
        const hide = result.svgElem?.visibility === 'hidden'

        result.linePath = { start: { x: start[0], y: start[1] }, end: { x: end[0], y: end[1] } }

        result.menuButton?.update?.(result, hide)
        result.hoverArea?.update?.(result, hide)
        result.tooltip?.update?.(result, hide)
        result.timestamp?.update?.(result, hide)

        // Clusters.setHighlight(result, true)

        // Render Menu when marker is commited
        if (result?.options?.isCommited) {
            const isInverted = result?.axis?.chart?.inverted
            const chartPlotBox = result?.axis?.chart?.plotBox

            result?.options?.onShowDetails(result?.options?.data, {
                marker: getMenuPosition(result.linePath, isInverted, chartPlotBox),
                chartPlotBox: chartPlotBox,
                isInverted: isInverted
            })

            result.options.isCommited = false
            result?.menuButton?.element?.setAttribute('visibility', 'visible')
        }

        return result
    }

    plotline.setFocus = function (this: ExtendedPlotline, val) {
        // Hide tooltip and timestamp elements.
        this.tooltip.attr({ visibility: val ? 'visible' : 'hidden' })
        this.timestamp.attr({ visibility: val ? 'visible' : 'hidden' })

        // Add special class fade to plotline element.
        if (!val) this.svgElem.element.classList.add('fade--')
        else this.svgElem.element.classList.remove('fade--')

        this.focus = val
    }

    plotline.destroy = function (this: ExtendedPlotline) {
        if (this.focus) Clusters.setHighlight(this, false)

        this.hoverArea.destroy()
        this.menuButton.destroy()
        this.timestamp.destroy()
        this.tooltip.destroy()

        this.hoverArea = null
        this.menuButton = null
        this.timestamp = null
        this.tooltip = null

        this.linePath = null

        return Object.getPrototypeOf(this).destroy.call(this)
    }

    Clusters.setHighlight(plotline, true)
}

function getMenuPosition(linePath: Line, isInverted: boolean, plotBox: number): Line {
    const startX = linePath?.start?.x
    const startY = linePath?.start?.y
    const endX = linePath?.end?.x
    const endY = linePath?.end?.y

    if (isInverted) {
        return plotBox?.height - linePath?.start?.y > 100
            ? { start: { x: startX, y: startY + 8 }, end: { x: endX, y: endY + 8 } }
            : { start: { x: startX, y: startY - 78 }, end: { x: endX, y: endY - 78 } }
    }

    return linePath?.start?.x > 200
        ? { start: { x: startX - 10, y: startY }, end: { x: endX - 10, y: endY } }
        : { start: { x: startX + 8, y: startY }, end: { x: endX + 8, y: endY } }
}
